import React, { FC, useState } from 'react'
import { makeStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { getFormattedPercentage } from 'apps/tms/routing-guide/utils/formatPercentage'
import { CheckFilledIcon, WarningIcon, RightArrowIcon } from 'transfix-icons'
import { StatsBoxOverlay } from './StatsBoxOverlay'

interface ITypeContent {
  icon?: JSX.Element
  label: string
  target?: string
}

interface IStatsBoxContent {
  [variant: string]: ITypeContent
}

const statsBoxContent: IStatsBoxContent = {
  doingGreat: {
    icon: <CheckFilledIcon className='check' />,
    label: 'Doing great! Keep it up!',
  },
  almostThere: {
    icon: <WarningIcon className='warning' />,
    label: 'You’re almost there. Keep going!',
  },
  needsImprovement: {
    icon: <WarningIcon className='warning' />,
    label: 'There’s room for improvement.',
  },
}

const statsBoxType: IStatsBoxContent = {
  onTimeRate: {
    label: 'On time rate',
    target: '95%',
  },
  trackingQuality: {
    label: 'Tracking quality',
    target: '95%',
  },
  lateCancellation: {
    label: 'Late cancellation',
    target: '<5%',
  },
}

const useStatsBoxStyles = makeStyles(theme => ({
  box: {
    position: 'relative',
    display: 'flex',
    border: `1px solid ${brandColors.coolGray2}`,
    padding: '20px 16px',
    borderRadius: 4,
    width: 332,
    background: brandColors.white,
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    cursor: 'pointer',

    '&:hover': {
      border: `1px solid ${brandColors.coolGray2}`,
      background: brandColors.coolGray2,
      boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.15)',
    },

    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  rateContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  target: {
    color: brandColors.coolGray6,
  },
  variantContent: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 20,
    color: brandColors.black,

    '& .almostThere, & .needsImprovement': {
      backgroundColor: brandColors.coolGray2,
      borderRadius: '50px',
      display: 'flex',
    },

    '& svg': {
      width: '21px',
      height: '21px',

      '&.check': {
        color: brandColors.green2,
      },

      '&.warning': {
        padding: theme.spacing(0.5),
        color: brandColors.coolGray5,
      },
    },
  },
}))

interface IStatsBoxProps {
  rate: number | undefined
  failure: number | undefined
  success: number | undefined
  variant: string
  getMetric: (rate: number, variant: string) => string
  isStale: boolean | null
}

const getEmptyStateRateValue = (variant: IStatsBoxProps['variant']): string =>
  variant === 'lateCancellation' ? '0%' : '--%'

export const StatsBox: FC<IStatsBoxProps> = ({
  failure = 0,
  getMetric,
  isStale = false,
  rate,
  success = 0,
  variant = '',
}) => {
  const classes = useStatsBoxStyles()
  const [open, isOpen] = useState<boolean>(false)
  const metric = typeof rate === 'number' && getMetric(rate, variant)

  const clickStatHandler = () => {
    isOpen(!open)
  }

  const onKeyDownHandler = () => {}

  const emptyRateValue = getEmptyStateRateValue(variant)

  const getRateValue = () =>
    typeof rate === 'number' ? getFormattedPercentage(rate || '0') : emptyRateValue

  return (
    <>
      <div
        role='button'
        tabIndex={0}
        className={classes.box}
        onClick={clickStatHandler}
        onKeyDown={onKeyDownHandler}>
        <div className={classes.rateContent}>
          <Typography variant='h2' component='span'>
            {isStale ? emptyRateValue : getRateValue()}
          </Typography>

          <Typography variant='subtitle1' component='span'>
            {statsBoxType[variant]?.label}
          </Typography>

          <Typography variant='body2' component='span' className={classes.target}>
            Target: {statsBoxType[variant]?.target}
          </Typography>

          {!isStale && metric && (
            <div className={classes.variantContent}>
              <div className={metric}>
                {typeof rate === 'number' && statsBoxContent[metric]?.icon}
              </div>
              <Typography variant='body2' component='span'>
                {typeof rate === 'number' && statsBoxContent[metric]?.label}
              </Typography>
            </div>
          )}
        </div>

        <RightArrowIcon />
      </div>

      {!isStale && metric && (
        <StatsBoxOverlay
          open={open}
          metric={metric}
          rate={rate}
          variant={variant}
          onClose={clickStatHandler}
          failure={failure}
          success={success}
        />
      )}
    </>
  )
}
